/**
 * Spanish Standard (UNE-EN 197-1) Cement Classifications
 * Mirrors the enum in the physics engine — kept here for shared frontend/backend use.
 */
export enum UneEn197CementType {
  CEM_I = 'CEM_I',
  CEM_FLY_ASH = 'CEM_FLY_ASH',
  CEM_GGBS = 'CEM_GGBS',
}

export enum AggregateType {
  QUARTZITE = 'QUARTZITE',
  GRANITE = 'GRANITE',
  BASALT = 'BASALT',
  LIMESTONE = 'LIMESTONE',
}

export interface StandardConcreteMixture {
  id: string;
  name: string;
  description: string;
  cementType: UneEn197CementType;
  aggregateType: AggregateType;
  isSandSiliceous: boolean;
  cementContentKg: number;
  waterContentKg: number;
  aggregateContentKg: number;
  wcRatio: number;
  flyAshPercentage: number;
  ggbsPercentage: number;
  fck28: number;          // 28-day characteristic compressive strength [MPa]
  T_datum: number;        // Nurse-Saul datum temperature [°C] — hydration ceases below this value
  plowman_A: number;      // Plowman strength constant A [MPa] — slope of log-linear curve
  plowman_B: number;      // Plowman strength constant B [MPa] — intercept
}

/**
 * Predefined typical concrete mixtures based on Spanish standards (Código Estructural).
 * HA = Hormigón Armado (Structural Concrete)
 * B = Blando (Soft consistency, ~50-90mm slump)
 * F = Fluido (Fluid consistency, ~100-150mm slump)
 */
export const SPANISH_STANDARD_MIXTURES: Record<string, StandardConcreteMixture> = {
  HA_30_B: {
    id: 'ha_30_b_20_xc2',
    name: 'HA-30/B/20/XC2',
    description: 'Hormigón Armado 30 MPa, Consistencia Blanda, Árido 20mm.',
    cementType: UneEn197CementType.CEM_I,
    aggregateType: AggregateType.LIMESTONE,
    isSandSiliceous: true,
    cementContentKg: 320,
    waterContentKg: 160,
    aggregateContentKg: 1850,
    wcRatio: 0.50,
    flyAshPercentage: 0,
    ggbsPercentage: 0,
    fck28: 30,
    T_datum: -5,
    plowman_A: 20,
    plowman_B: -20,
  },
  HA_30_F: {
    id: 'ha_30_f_20_xc2',
    name: 'HA-30/F/20/XC2',
    description: 'Hormigón Armado 30 MPa, Consistencia Fluida, Árido 20mm.',
    cementType: UneEn197CementType.CEM_I,
    aggregateType: AggregateType.LIMESTONE,
    isSandSiliceous: true,
    cementContentKg: 340,
    waterContentKg: 170,
    aggregateContentKg: 1800,
    wcRatio: 0.50,
    flyAshPercentage: 0,
    ggbsPercentage: 0,
    fck28: 30,
    T_datum: -5,
    plowman_A: 16,
    plowman_B: -10,
  },
  HA_40_B: {
    id: 'ha_40_b_20_xc4',
    name: 'HA-40/B/20/XC4',
    description: 'Hormigón Armado 40 MPa, Consistencia Blanda, Árido 20mm.',
    cementType: UneEn197CementType.CEM_I,
    aggregateType: AggregateType.LIMESTONE,
    isSandSiliceous: true,
    cementContentKg: 350,
    waterContentKg: 157.5,
    aggregateContentKg: 1850,
    wcRatio: 0.45,
    flyAshPercentage: 0,
    ggbsPercentage: 0,
    fck28: 40,
    T_datum: -5,
    plowman_A: 20,
    plowman_B: -30,
  },
  HA_40_F: {
    id: 'ha_40_f_20_xc4',
    name: 'HA-40/F/20/XC4',
    description: 'Hormigón Armado 40 MPa, Consistencia Fluida, Árido 20mm.',
    cementType: UneEn197CementType.CEM_I,
    aggregateType: AggregateType.LIMESTONE,
    isSandSiliceous: true,
    cementContentKg: 380,
    waterContentKg: 171,
    aggregateContentKg: 1800,
    wcRatio: 0.45,
    flyAshPercentage: 0,
    ggbsPercentage: 0,
    fck28: 40,
    T_datum: -5,
    plowman_A: 20,
    plowman_B: -30,
  },
};
